<template>
  <AppModalDialog hash="sign-in" class="signIn">
    <Contact
      v-if="step === 'contact'"
      :message="message || signInMessage"
      @next="onContactNext"
      @success="onSuccess"
      @update:canSubmit="contactCanSubmit = $event"
    />
    <Verify
      v-else-if="step === 'verify'"
      :email="formState.email"
      :is-existing-user="formState.isExistingUser"
      @success="onVerified"
      @needsName="step = 'name'"
      @update:canSubmit="verifyCanSubmit = $event"
    />
    <Name v-else @success="onSuccess" @update:canSubmit="nameCanSubmit = $event" />
    <template #footer>
      <template v-if="step === 'contact'">
        <!-- how it works, just above Continue (Seán's mockup, 2026-10-22; issue SI2) -->
        <p class="note">
          <span>We'll send a verification code.</span> <span>No password needed.</span>
        </p>
        <ButtonBespoke
          outstanding
          :disabled="!contactCanSubmit"
          @click="submitForm('sign-in-contact-form')"
        >Continue ▸</ButtonBespoke>
      </template>
      <div v-else-if="step === 'verify'" class="verify-buttons">
        <ButtonBespoke @click="step = 'contact'">◂ Start Again</ButtonBespoke>
        <ButtonBespoke
          outstanding
          :disabled="!verifyCanSubmit"
          @click="submitForm('sign-in-verify-form')"
        >Continue ▸</ButtonBespoke>
      </div>
      <ButtonBespoke
        v-else-if="step === 'name'"
        outstanding
        :disabled="!nameCanSubmit"
        @click="submitForm('sign-in-name-form')"
      >Continue ▸</ButtonBespoke>
    </template>
  </AppModalDialog>
</template>

<script setup>
import {inject, reactive, ref, watch, nextTick} from "vue";
import {useRoute, useRouter} from "vue-router";
import AppModalDialog from "@/components/App/Modal/Dialog/index";
import ButtonBespoke from "@/components/ButtonBespoke";
import Contact from "@/components/App/Modal/SignIn/Contact";
import Verify from "@/components/App/Modal/SignIn/Verify";
import Name from "@/components/App/Modal/SignIn/Name";
import {getPendingReservedEvent} from "@/composables/reservedPlace";
import {redirectToRegistration} from "@/composables/useRedirectToRegistration";
import {
  clearSignInRequest, signInMessage, takeSignInRequest
} from "@/composables/useSignInRequest";

defineProps(["message"]);
const route = useRoute();
const router = useRouter();
const {isSignedIn, isLoaded} = inject("authentication");
let isLeaving = false; // onSuccess runs once: after the code, isSignedIn turns true here as well
const step = ref("contact");
const formState = reactive({email: "", isExistingUser: false, isSigningUp: false});
const contactCanSubmit = ref(false);
const verifyCanSubmit = ref(false);
const nameCanSubmit = ref(false);

watch(
  () => route.hash,
  (hash) => {
    if (hash !== "#sign-in") {
      clearSignInRequest(); // closed without signing in: the download or video doesn't follow
      step.value = "contact";
      Object.assign(formState, {email: "", isExistingUser: false, isSigningUp: false});
    }
  },
);

function onContactNext({email, isExistingUser}) {
  formState.email = email;
  formState.isExistingUser = isExistingUser;
  formState.isSigningUp = !isExistingUser;
  step.value = "verify";
}

// a new account (Seán, 2026-11-02): on to its details (name, affiliation) before #profile, so
// they're asked for, not left for the profile to prompt; an existing one carries on
function onVerified() {
  if (formState.isSigningUp) return (step.value = "name");
  onSuccess();
}

function submitForm(formId) {
  document.getElementById(formId)?.requestSubmit();
}

// signed in: what asked for the sign-in carries on; otherwise #profile, or with a 2-for-1 place
// still to complete, #register instead (as the invitation link), so the colleague can't end up
// with nothing on the Events tab and no videos
async function onSuccess() {
  if (isLeaving) return;
  isLeaving = true;
  setTimeout(() => (isLeaving = false), 1000);
  // asked for by a download or a free video: close, and that carries on (useSignInRequest.js)
  const waiting = takeSignInRequest();
  if (waiting) {
    // the address's query kept (e.g. /feedback?event=…)
    await router.replace({path: route.path, query: route.query, hash: ""});
    return waiting.onSignedIn?.();
  }
  const reservedEvent = getPendingReservedEvent(window.Clerk?.user);
  if (reservedEvent) return redirectToRegistration(reservedEvent.split("-")[0]);
  await router.replace({path: route.path, hash: "#profile"});
  await nextTick();
  if (route.hash !== "#profile") {
    router.replace({path: route.path, hash: "#sign-in"});
  }
}

// #sign-in opened while already signed in (e.g. by a link): on as after signing in, rather than
// a form that can only answer "Session Already Exists" (2026-10-13)
// (not a new account just signed in: its details come first, onVerified)
watch([isLoaded, isSignedIn, () => route.hash], ([loaded, signedIn, hash]) => {
  if (formState.isSigningUp) return;
  if (loaded && signedIn && hash === "#sign-in") onSuccess();
}, {immediate: true});
</script>

<style scoped>
/* the dialog's size, padding, footer and disabled button: every dialog's (Dialog/, 2026-10-23) */
:deep(.signIn footer.hasContent) {
  container-type: inline-size; /* the note's size follows its width */
}
/* an aside (DESIGN.md § 2), quieter than the text above: the lightest grey still readable on
   the dialog (#666 on #e4e4e4: 4.5:1) */
/* issue SI2 (Seán, 2026-10-22): one line, 14px, down to 12px as the width narrows (the line is
   23.6em wide in Nunito: 330px at 14px); narrower still, 14px on two lines, the break between
   the sentences */
.note {
  /* 22px below the field, with the dialog's own; the note is the footer's, so it gives back the
     1rem added above every footer (2026-10-23): it belongs with the field */
  margin: calc(6px - 1rem) 0 12px;
  text-align: center;
  font-size: clamp(12px, 100cqw / 23.8, 14px);
  line-height: 1.4; /* the colour: every footer note's (Dialog/index.vue) */
}
.note span {
  white-space: nowrap;
}
@container (max-width: 285px) {
  .note {
    font-size: 14px;
  }
}
.verify-buttons {
  display: flex;
  gap: 0.75rem;
}
.verify-buttons > * {
  flex: 1;
}
</style>
